import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { UserCheck, QrCode, Copy, Download, Plus, Trash2, Eye, Package, Users, AlertCircle, Printer, Maximize2, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CompGuestSection({ eventId, compGuests, tickets, event, user, onRefresh }) {
  const [showSelfCheckinQR, setShowSelfCheckinQR] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', nickname: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [availability, setAvailability] = useState(null);
  const [fullscreenQR, setFullscreenQR] = useState(false);

  const selfCheckinUrl = `${window.location.origin}/e/${eventId}/checkin`;
  const masterCheckinUrl = user?.user_id ? `${window.location.origin}/h/${user.user_id}/checkin` : '';
  const token = localStorage.getItem('session_token');
  const headers = { Authorization: `Bearer ${token}` };

  const entryTiers = useMemo(() =>
    (event?.pass_tiers || []).filter(t => (t.type || 'entry') === 'entry'),
    [event]
  );
  const addonTiers = useMemo(() =>
    (event?.pass_tiers || []).filter(t => t.type === 'addon'),
    [event]
  );

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/availability`);
      setAvailability(res.data);
    } catch { /* silent */ }
  }, [eventId]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const handlePrintQR = () => {
    const hostName = user?.name || 'VelvetGate Host';
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(masterCheckinUrl)}`;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Please allow popups to print'); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>Comp Guest Check-In QR</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
@page{size:letter;margin:0.75in}
body{font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;background:#fff;color:#0B0B0C;display:flex;align-items:center;justify-content:center;min-height:100vh}
.card{text-align:center;max-width:480px;margin:0 auto;padding:48px 32px}
.badge{display:inline-block;font-size:11px;letter-spacing:4px;font-weight:700;color:#8C6B1F;text-transform:uppercase;border:2px solid #8C6B1F;padding:6px 20px;border-radius:2px;margin-bottom:32px}
.qr{margin:0 auto 28px;padding:16px;border:3px solid #0B0B0C;border-radius:4px;display:inline-block}
.qr img{width:320px;height:320px;display:block}
h1{font-size:32px;font-weight:800;letter-spacing:-0.5px;margin-bottom:8px;color:#0B0B0C}
.host{font-size:16px;color:#555;margin-bottom:24px}
.inst{font-size:18px;color:#333;margin-bottom:32px;font-weight:500}
.foot{font-size:11px;color:#999;letter-spacing:2px;text-transform:uppercase;border-top:1px solid #ddd;padding-top:16px;margin-top:16px}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="card">
<div class="badge">VelvetGate</div>
<div class="qr"><img src="${qrUrl}" alt="QR" /></div>
<h1>Comp Guest Check-In</h1>
<p class="host">${hostName}</p>
<p class="inst">Scan the QR code above to check in</p>
<p class="foot">Powered by VelvetGate</p>
</div>
<script>window.onload=function(){setTimeout(function(){window.print()},600)}<\/script>
</body></html>`);
    w.document.close();
  };

  // Build lookup: tier/addon id -> remaining
  const remainingMap = useMemo(() => {
    if (!availability) return {};
    const map = {};
    (availability.tiers || []).forEach(t => { map[t.id] = t.remaining; });
    (availability.addons || []).forEach(a => { map[a.id] = a.remaining; });
    return map;
  }, [availability]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/comp-guests/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API}/comp-guests`, form, { headers });
      }
      toast.success(editingId ? 'Updated' : 'Guest added');
      setForm({ email: '', name: '', nickname: '', notes: '' });
      setEditingId(null);
      setShowForm(false);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (guestId) => {
    if (!confirm('Remove this guest from your saved list?')) return;
    try {
      await axios.delete(`${API}/comp-guests/${guestId}`, { headers });
      toast.success('Removed');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const toggleAddOn = (addonId) => {
    setSelectedAddOns(prev => {
      const next = { ...prev };
      if (next[addonId]) { delete next[addonId]; }
      else { next[addonId] = 1; }
      return next;
    });
  };

  // Count how many selected guests don't already have tickets
  const eligibleCount = useMemo(() => {
    return selected.filter(gId => {
      const g = compGuests.find(c => c.guest_id === gId);
      if (!g) return false;
      return !tickets.some(t => t.guest_email?.toLowerCase() === g.email && t.is_comp && t.status !== 'revoked');
    }).length;
  }, [selected, compGuests, tickets]);

  const selectedTier = entryTiers.find(t => t.id === selectedTierId);
  const attendeesPerPass = selectedTier?.attendees_per_pass || 1;
  const totalAttendees = eligibleCount * attendeesPerPass;

  // Inventory warnings
  const tierRemaining = selectedTierId ? remainingMap[selectedTierId] : null;
  const tierWarning = tierRemaining !== null && tierRemaining !== undefined && tierRemaining >= 0 && eligibleCount > tierRemaining;

  const handleApply = async () => {
    if (selected.length === 0) { toast.error('Select guests to add'); return; }
    setApplying(true);
    try {
      const payload = { guest_ids: selected };
      if (selectedTierId) payload.pass_tier_id = selectedTierId;
      const addOnsList = Object.entries(selectedAddOns)
        .filter(([, qty]) => qty > 0)
        .map(([id, qty]) => ({ add_on_id: id, quantity: qty }));
      if (addOnsList.length > 0) payload.add_ons = addOnsList;

      const res = await axios.post(`${API}/events/${eventId}/apply-comp-list`, payload, { headers });
      const parts = [`${res.data.created_count} comp ticket${res.data.created_count !== 1 ? 's' : ''} created`];
      if (selectedTier) parts.push(`(${selectedTier.tier_name})`);
      if (res.data.skipped_count > 0) parts.push(`${res.data.skipped_count} skipped`);
      toast.success(parts.join(' — '));
      setSelected([]);
      setSelectedTierId('');
      setSelectedAddOns({});
      onRefresh();
      fetchAvailability();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setApplying(false); }
  };

  const toggle = (guestId) => setSelected(prev => prev.includes(guestId) ? prev.filter(g => g !== guestId) : [...prev, guestId]);
  const toggleAll = () => setSelected(prev => prev.length === compGuests.length ? [] : compGuests.map(g => g.guest_id));

  const hasSelection = selected.length > 0;

  return (
    <div className="mt-10 border-t border-[#1A1A1A] pt-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <UserCheck className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-[#EAE7E1] tracking-tight" data-testid="comp-guest-list-heading">Comp Guest List</h2>
            <p className="text-xs text-[#777]">Reusable guest list across events</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button onClick={() => setShowSelfCheckinQR(!showSelfCheckinQR)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] hover:bg-[#1A1A1A] rounded-sm" data-testid="self-checkin-qr-toggle">
            <QrCode className="w-4 h-4 mr-2" />Self Check-In QR
          </Button>
          <Button onClick={() => { setShowForm(!showForm); if (showForm) { setEditingId(null); setForm({ email: '', name: '', nickname: '', notes: '' }); } }} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="add-comp-guest-button">
            <Plus className="w-4 h-4 mr-2" />Add Guest
          </Button>
        </div>
      </div>

      {showSelfCheckinQR && (
        <div className="mb-6 space-y-4" data-testid="self-checkin-qr-section">
          {/* Master QR */}
          {masterCheckinUrl && (
            <div className="p-5 bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-sm" data-testid="master-qr-section">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-[#D4AF37]">Master Comp Check-In QR</h3>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">Reusable</span>
              </div>
              <p className="text-xs text-[#777] mb-4">Works across all your events. Best for recurring venues or permanent posting.</p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="bg-white p-3 rounded" data-testid="master-qr-image">
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(masterCheckinUrl)}`} alt="Master Check-In QR" className="w-48 h-48" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="text-xs text-[#AFAFAF] break-all bg-[#050505] p-2 rounded border border-[#1A1A1A]" data-testid="master-checkin-url">{masterCheckinUrl}</div>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => { navigator.clipboard.writeText(masterCheckinUrl); toast.success('Master link copied'); }} size="sm" variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid="copy-master-link"><Copy className="w-3 h-3 mr-1.5" />Copy Link</Button>
                    <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&format=png&data=${encodeURIComponent(masterCheckinUrl)}`} download="master-comp-checkin.png" className="inline-flex items-center px-3 h-8 text-sm border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors" data-testid="download-master-qr"><Download className="w-3 h-3 mr-1.5" />Download</a>
                    <Button onClick={handlePrintQR} size="sm" variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid="print-master-qr"><Printer className="w-3 h-3 mr-1.5" />Print QR</Button>
                    <Button onClick={() => setFullscreenQR(true)} size="sm" variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid="fullscreen-master-qr"><Maximize2 className="w-3 h-3 mr-1.5" />Display</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Event-Specific QR */}
          <div className="p-5 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="event-qr-section">
            <h3 className="text-sm font-semibold text-[#EAE7E1] mb-1">Event-Specific Check-In QR</h3>
            <p className="text-xs text-[#777] mb-4">Only works for this event. Best for one-off events or different venues.</p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="bg-white p-3 rounded" data-testid="event-qr-image">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(selfCheckinUrl)}`} alt="Event Check-In QR" className="w-48 h-48" />
              </div>
              <div className="flex-1 space-y-3">
                <div className="text-xs text-[#AFAFAF] break-all bg-[#050505] p-2 rounded border border-[#1A1A1A]" data-testid="self-checkin-url">{selfCheckinUrl}</div>
                <div className="flex gap-2">
                  <Button onClick={() => { navigator.clipboard.writeText(selfCheckinUrl); toast.success('Event link copied'); }} size="sm" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm" data-testid="copy-checkin-link"><Copy className="w-3 h-3 mr-1.5" />Copy Link</Button>
                  <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&format=png&data=${encodeURIComponent(selfCheckinUrl)}`} download={`self-checkin-${eventId}.png`} className="inline-flex items-center px-3 h-8 text-sm border border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm transition-colors" data-testid="download-checkin-qr"><Download className="w-3 h-3 mr-1.5" />Download</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSave} className="mb-6 p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="comp-guest-form">
          <div>
            <label className="block text-xs text-[#777] mb-1">Email *</label>
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" required placeholder="guest@email.com" className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm h-9 text-sm" data-testid="comp-guest-email" />
          </div>
          <div>
            <label className="block text-xs text-[#777] mb-1">Name</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm h-9 text-sm" data-testid="comp-guest-name" />
          </div>
          <div>
            <label className="block text-xs text-[#777] mb-1">Nickname</label>
            <Input value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} placeholder="Screen name / alias" className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm h-9 text-sm" data-testid="comp-guest-nickname" />
          </div>
          <div>
            <label className="block text-xs text-[#777] mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="VIP, press, sponsor, dietary..." rows={2} className="w-full bg-[#050505] border border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm px-3 py-2 text-sm resize-none focus:border-[#D4AF37] focus:outline-none" data-testid="comp-guest-notes" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold h-9 text-sm" data-testid="save-comp-guest">{saving ? 'Saving...' : editingId ? 'Update' : 'Add'}</Button>
            {editingId && <Button type="button" onClick={() => { setEditingId(null); setForm({ email: '', name: '', nickname: '', notes: '' }); }} variant="outline" className="border-[#1A1A1A] text-[#777] h-9 text-sm rounded-sm">Cancel</Button>}
          </div>
        </form>
      )}

      {compGuests.length > 0 ? (
        <div>
          {/* Guest list with selection */}
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-xs text-[#777] cursor-pointer">
              <input type="checkbox" checked={selected.length === compGuests.length} onChange={toggleAll} className="rounded" data-testid="select-all-comp-guests" />
              Select all ({compGuests.length})
            </label>
          </div>

          <div className="space-y-1.5 mb-4" data-testid="comp-guest-list">
            {compGuests.map(g => {
              const hasTicket = tickets.some(t => t.guest_email?.toLowerCase() === g.email && t.is_comp && t.status !== 'revoked');
              return (
                <div key={g.guest_id} className={`flex items-center gap-3 px-3 py-2 rounded-sm border ${hasTicket ? 'bg-emerald-500/5 border-emerald-500/20' : selected.includes(g.guest_id) ? 'bg-[#D4AF37]/5 border-[#D4AF37]/20' : 'bg-[#0A0A0A] border-[#1A1A1A]'}`} data-testid={`comp-guest-${g.guest_id}`}>
                  <input type="checkbox" checked={selected.includes(g.guest_id)} onChange={() => toggle(g.guest_id)} className="rounded" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#EAE7E1] truncate">{g.nickname || g.name || g.email}</span>
                      {g.nickname && g.name && <span className="text-xs text-[#555]">({g.name})</span>}
                      {hasTicket && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">Has Ticket</span>}
                    </div>
                    {(g.nickname || g.name) && <div className="text-xs text-[#555]">{g.email}</div>}
                    {g.notes && <div className="text-[10px] text-[#777] truncate mt-0.5" title={g.notes}>{g.notes}</div>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditingId(g.guest_id); setForm({ email: g.email, name: g.name, nickname: g.nickname || '', notes: g.notes || '' }); setShowForm(true); }} className="text-[#555] hover:text-[#AFAFAF] p-1" data-testid={`edit-comp-guest-${g.guest_id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDelete(g.guest_id)} className="text-[#555] hover:text-[#C44545] p-1" data-testid={`delete-comp-guest-${g.guest_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Assignment Panel — always visible when guests are selected */}
          {hasSelection && (
            <div className="p-4 bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-sm space-y-4" data-testid="comp-assignment-panel">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm font-semibold text-[#EAE7E1]">Assign to Event</span>
                  <span className="text-xs text-[#777]">({eligibleCount} eligible of {selected.length} selected)</span>
                </div>
                {eligibleCount > 0 && (
                  <div className="flex items-center gap-2 text-xs text-[#777]">
                    <Users className="w-3.5 h-3.5" />
                    {totalAttendees} attendee{totalAttendees !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* Entry Tier Selection */}
              {entryTiers.length > 0 && (
                <div>
                  <label className="block text-[10px] text-[#777] mb-2 font-semibold uppercase tracking-wider">Entry Pass</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="comp-tier-options">
                    <button
                      onClick={() => setSelectedTierId('')}
                      className={`p-3 rounded-sm border text-left transition-colors ${!selectedTierId ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5' : 'border-[#1A1A1A] hover:border-[#333]'}`}
                      data-testid="comp-tier-default"
                    >
                      <div className="text-sm font-medium text-[#EAE7E1]">Comp Guest List</div>
                      <div className="text-[10px] text-[#555] mt-0.5">Default &middot; 1 attendee</div>
                    </button>
                    {entryTiers.map(t => {
                      const remaining = remainingMap[t.id];
                      const isSelected = selectedTierId === t.id;
                      const soldOut = remaining !== undefined && remaining >= 0 && remaining === 0;
                      return (
                        <button
                          key={t.id}
                          onClick={() => !soldOut && setSelectedTierId(t.id)}
                          disabled={soldOut}
                          className={`p-3 rounded-sm border text-left transition-colors ${isSelected ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5' : soldOut ? 'border-[#1A1A1A] opacity-40 cursor-not-allowed' : 'border-[#1A1A1A] hover:border-[#333]'}`}
                          data-testid={`comp-tier-${t.id}`}
                        >
                          <div className="text-sm font-medium text-[#EAE7E1]">{t.tier_name}</div>
                          <div className="text-[10px] text-[#555] mt-0.5">
                            {t.attendees_per_pass || 1} attendee{(t.attendees_per_pass || 1) > 1 ? 's' : ''}
                            {t.is_unlimited ? ' · Unlimited' : remaining !== undefined && remaining >= 0 ? ` · ${remaining} remaining` : ''}
                            {soldOut && ' · SOLD OUT'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {tierWarning && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-[#FFB800]" data-testid="tier-inventory-warning">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Only {tierRemaining} {selectedTier?.tier_name} remaining — {eligibleCount} guests selected
                    </div>
                  )}
                </div>
              )}

              {/* Add-on Selection */}
              {addonTiers.length > 0 && (
                <div>
                  <label className="block text-[10px] text-[#777] mb-2 font-semibold uppercase tracking-wider">Include Add-Ons</label>
                  <div className="space-y-2">
                    {addonTiers.map(a => {
                      const remaining = remainingMap[a.id];
                      const isChecked = !!selectedAddOns[a.id];
                      const soldOut = remaining !== undefined && remaining >= 0 && remaining === 0;
                      const inventoryWarning = isChecked && remaining !== undefined && remaining >= 0 && eligibleCount > remaining;
                      return (
                        <div key={a.id}>
                          <label className={`flex items-center gap-3 p-2.5 rounded-sm border cursor-pointer transition-colors ${isChecked ? 'border-[#D4AF37]/30 bg-[#D4AF37]/5' : soldOut ? 'border-[#1A1A1A] opacity-40 cursor-not-allowed' : 'border-[#1A1A1A] hover:border-[#333]'}`} data-testid={`comp-addon-${a.id}`}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => !soldOut && toggleAddOn(a.id)}
                              disabled={soldOut}
                              className="rounded"
                            />
                            <div className="flex-1">
                              <span className="text-sm text-[#EAE7E1]">{a.tier_name}</span>
                              <span className="text-xs text-[#555] ml-2">
                                ${a.price}
                                {a.is_unlimited ? ' · Unlimited' : remaining !== undefined && remaining >= 0 ? ` · ${remaining} remaining` : ''}
                                {soldOut && ' · SOLD OUT'}
                              </span>
                            </div>
                          </label>
                          {inventoryWarning && (
                            <div className="flex items-center gap-1.5 mt-1 ml-8 text-xs text-[#FFB800]" data-testid={`addon-inventory-warning-${a.id}`}>
                              <AlertCircle className="w-3 h-3" />
                              Only {remaining} remaining — {eligibleCount} guests need this
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Apply button */}
              <div className="pt-2 border-t border-[#1A1A1A]">
                <Button onClick={handleApply} disabled={applying || eligibleCount === 0} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold text-sm h-9 w-full sm:w-auto" data-testid="apply-comp-list-button">
                  {applying ? 'Creating tickets...' : eligibleCount === 0
                    ? 'All selected guests already have tickets'
                    : `Create ${eligibleCount} Comp Ticket${eligibleCount !== 1 ? 's' : ''}${selectedTier ? ` (${selectedTier.tier_name})` : ''}`
                  }
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : !showForm ? (
        <div className="text-center py-8 text-[#555]" data-testid="comp-guest-empty">
          <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No saved comp guests yet</p>
          <p className="text-xs mt-1">Add guests to your reusable comp list</p>
        </div>
      ) : null}

      {/* Fullscreen QR Overlay */}
      {fullscreenQR && masterCheckinUrl && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center" data-testid="fullscreen-qr-overlay" onClick={() => setFullscreenQR(false)}>
          <button onClick={() => setFullscreenQR(false)} className="absolute top-6 right-6 text-white/50 hover:text-white p-2" data-testid="close-fullscreen-qr">
            <X className="w-8 h-8" />
          </button>
          <div className="text-center">
            <div className="text-xs text-[#D4AF37] font-semibold tracking-[4px] uppercase mb-6">VelvetGate</div>
            <div className="bg-white p-6 rounded-lg inline-block mb-6">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(masterCheckinUrl)}`} alt="Master Check-In QR" className="w-72 h-72 sm:w-96 sm:h-96" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Comp Guest Check-In</h2>
            <p className="text-lg text-white/60">{user?.name || 'VelvetGate Host'}</p>
            <p className="text-sm text-white/40 mt-4">Scan to check in &middot; Tap anywhere to close</p>
          </div>
        </div>
      )}
    </div>
  );
}
